import React from 'react'
import { Box, Checkbox, Chip, FormControlLabel, Typography } from '@mui/material'
import {
  groupState,
  toggleGroup,
  toggleCampaign,
  activeCampaignCount,
  isSentinel,
  NO_PRODUCT_GROUP,
  UNCLAIMED_GROUP,
  type CampaignTreeGroup,
} from './campaignTreeModel'
import { sentinelDescription } from './CampaignFilter'

export interface CampaignTreeProps {
  groups: CampaignTreeGroup[]
  /** The ONE selection. Flat campaign ids — there is no product selection. */
  selected: string[]
  onChange: (next: string[]) => void
  /** Entity counts per campaign, from the filter summary. Optional. */
  counts?: Record<string, number>
  disabled?: boolean
  isLoading?: boolean
}

/** Copy for the two synthetic groups. Real products explain themselves. */
const GROUP_NOTES: Record<string, string> = {
  [UNCLAIMED_GROUP]:
    'Campaigns no department claims. They appear here so they are still reachable.',
  [NO_PRODUCT_GROUP]: 'Records with no campaign of their own.',
}

/**
 * The admin campaign picker: departments as GROUPINGS over one campaign
 * selection (T3.4).
 *
 * 🔴 PRODUCT IS A GROUPING, NOT A SECOND FILTER. Ticking a department writes its
 * campaigns into the single selection; nothing but campaign ids leaves this
 * component, and no request carries a product field. The earlier design had a
 * product multi-select AND a campaign multi-select, and they fight — a product
 * IS a set of campaigns, so "products = {Brand}" and "campaigns = {Cinema's}"
 * has no coherent answer.
 *
 * ✅ The tri-state parent is only well-defined because no campaign may be
 * claimed twice, which the manifest loader enforces at load. That check is
 * load-bearing for this component, not just for the data model.
 *
 * ⚠ THIS COMPONENT IS FOR THE ADMIN PAGE ONLY. The product→campaign map it
 * renders is Sky-internal commercial information; the server serves it for
 * selectable products only and `SkyProductCard` withholds it everywhere else.
 */
export const CampaignTree: React.FC<CampaignTreeProps> = ({
  groups,
  selected,
  onChange,
  counts,
  disabled = false,
  isLoading = false,
}) => {
  const active = activeCampaignCount(groups, selected)

  if (isLoading) {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Loading campaigns…
        </Typography>
      </Box>
    )
  }
  if (groups.length === 0) return null

  return (
    <Box sx={{ px: 3, py: 2 }} data-testid="sky-campaign-tree">
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Campaigns
        </Typography>
        {/* Neutral AND fully-selected both read "All campaigns", because both
            send no constraint — the badge must never claim a filter that
            changes nothing. */}
        <Typography variant="caption" color="text.secondary">
          {active === 0 ? 'All campaigns' : `${active} selected`}
        </Typography>
      </Box>

      {groups.map((group) => {
        const state = groupState(group, selected)
        const note = GROUP_NOTES[group.slug]

        return (
          <Box key={group.slug} sx={{ mb: 1.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  disabled={disabled}
                  checked={state === 'all'}
                  indeterminate={state === 'some'}
                  onChange={() => onChange(toggleGroup(group, selected))}
                  inputProps={{ 'aria-label': `Select all campaigns in ${group.name}` }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {group.name}
                </Typography>
              }
              data-testid={`sky-campaign-group-${group.slug}`}
            />
            {note && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', ml: 4, mt: -0.5, mb: 0.5 }}
              >
                {note}
              </Typography>
            )}

            <Box sx={{ ml: 3 }}>
              {group.campaigns.map((campaign) => {
                const count = counts?.[campaign.value]
                // The sentinels get their explanation here for the same reason
                // they get one in CampaignFilter: "Unattributed" with no
                // explanation reads as a bug rather than a category, and a
                // zero-count option reads as broken rather than empty.
                const description = isSentinel(campaign.value)
                  ? sentinelDescription(campaign.value, count ?? 0)
                  : undefined

                return (
                  <Box key={campaign.value}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          disabled={disabled}
                          checked={selected.includes(campaign.value)}
                          onChange={() => onChange(toggleCampaign(campaign.value, selected))}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{campaign.label}</Typography>
                          {count !== undefined && (
                            <Chip
                              label={count.toLocaleString()}
                              size="small"
                              variant="outlined"
                              sx={{ height: 18, fontSize: '0.65rem' }}
                            />
                          )}
                        </Box>
                      }
                      data-testid={`sky-campaign-option-${campaign.value}`}
                    />
                    {description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', ml: 4, mt: -0.5, mb: 0.5 }}
                      >
                        {description}
                      </Typography>
                    )}
                  </Box>
                )
              })}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default CampaignTree
